/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { ActionType } from 'cio/models/action';
import { CampaignType, RESTRICTED_ACTION_TYPES } from 'cio/models/campaign';
import { deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import { ProcessedMetricItem } from 'cio/utils/metrics/analysis';
import { MeasureType } from 'cio/utils/metrics/measure';
import { cached } from '@glimmer/tracking';
import { Channel } from 'cio/utils/metrics/common';
import joinWords from 'cio/utils/join-words';

interface AnalysisPageTableRowArgs {
  item?: ProcessedMetricItem;
  id: string;
  data: {
    per_channel_metrics: Record<string, number | number[]>;
    delivery_metrics: Record<string, number | number[]>;
    journey_metrics: Record<string, number | number[]>;
    per_subscription_topic_metrics: Record<string, number>;
  };
  type: 'all' | 'newsletter' | 'transactional_message' | CampaignType;
  totalTypes: {
    itemTypes: (CampaignType | 'newsletter' | 'transactional_message')[];
    newsletterChannels: Channel[];
    transactionalChannels: Channel[];
    anyConversions: boolean;
  } | null;
  showSpinner: boolean;
  measures: { label: string; measureType: string; measureName: string; value: string; available: boolean }[];
  firstCellClasses: string;
  cellClasses: string;
  selectedChannels: Channel[];
}

export default class AnalysisPageTableRow extends Component<AnalysisPageTableRowArgs> {
  private channelsForType(type: CampaignType | 'newsletter' | 'transactional_message', channel?: Channel) {
    if (type === 'newsletter') {
      return this.args.selectedChannels.filter((c) => c === channel);
    }

    if (type === 'transactional_message') {
      return this.args.selectedChannels.filter((c) => c === channel);
    }

    let restrictedActionTypes = RESTRICTED_ACTION_TYPES[type] ?? [];

    return this.args.selectedChannels.filter((c) => !restrictedActionTypes.includes(`${c}_action` as ActionType));
  }

  private mapChannelsByType(channels: Channel[], type: 'newsletter' | 'transactional_message') {
    return channels.flatMap((channel) => {
      return this.channelsForType(type, channel);
    });
  }

  @cached
  get availableChannels() {
    if (this.args.type === 'all' && this.args.totalTypes != null) {
      let channels: Channel[] = [];

      if (this.args.totalTypes.itemTypes.includes('newsletter')) {
        channels = this.mapChannelsByType(this.args.totalTypes.newsletterChannels, 'newsletter');
      }

      if (this.args.totalTypes.itemTypes.includes('transactional_message')) {
        channels = [
          ...channels,
          ...this.mapChannelsByType(this.args.totalTypes.transactionalChannels, 'transactional_message'),
        ];
      }

      let typesWithoutNewsletterAndTransactional = this.args.totalTypes.itemTypes.filter(
        (t) => t !== 'newsletter' && t !== 'transactional_message',
      );

      channels = [
        ...channels,
        ...typesWithoutNewsletterAndTransactional.reduce<Channel[]>((acc, type) => {
          return [...acc, ...this.channelsForType(type)];
        }, []),
      ];

      return channels;
    }

    if (this.args.item == null) {
      return this.args.selectedChannels;
    }

    return this.channelsForType(this.args.item.type, this.args.item.channel as Channel | undefined);
  }

  get totalRowTooltipType() {
    let types = (this.args.totalTypes?.itemTypes ?? []).map((t) => {
      if (t === 'newsletter') {
        return 'newsletters';
      }

      if (t === 'transactional_message') {
        return 'messages';
      }

      if (t === CampaignType.APITriggered) {
        return 'broadcasts';
      }

      return 'campaigns';
    });

    return joinWords(types);
  }

  @cached
  get campaignType() {
    if (
      this.args.item == null ||
      this.args.item?.type === 'newsletter' ||
      this.args.item?.type === 'transactional_message'
    ) {
      return null;
    }

    return this.args.item?.type;
  }

  @cached
  get optionalConversions() {
    if (this.args.type === 'all') {
      return {
        webhook_action: true,
      };
    }

    return {
      webhook_action: this.args.item?.conversion,
    };
  }

  @cached
  get availableMeasures() {
    let { availableChannels } = this;
    let deliveryStates = deliveryStatesAndActionsPerType(this.args.type === 'all' ? true : this.args.item?.conversion);

    let measures = availableChannels.reduce<string[]>((acc, channel) => {
      let measures = deliveryStates[`${channel}_action` as ActionType] ?? [];
      return [...acc, ...measures].uniq();
    }, []);

    if (
      (this.args.type !== 'all' && !this.args.item?.conversion) ||
      (this.args.type === 'all' && !this.args.totalTypes?.anyConversions)
    ) {
      measures = measures.filter((m) => m !== 'converted');
    }

    return measures;
  }

  @cached
  get measuresWithAvailability() {
    return this.args.measures.map((m) => {
      if (m.measureType === MeasureType.JourneyMetrics) {
        let isAvailable = this.args.item?.type !== 'newsletter' && this.args.item?.type !== 'transactional_message';

        if (
          m.measureName === 'converted' &&
          ((this.args.type !== 'all' && !this.args.item?.conversion) ||
            (this.args.type === 'all' && !this.args.totalTypes?.anyConversions))
        ) {
          isAvailable = false;
        }

        return {
          ...m,
          isAvailable,
        };
      }

      return {
        ...m,
        isAvailable: this.availableMeasures.includes(m.measureName),
      };
    });
  }
}
